import { motion } from "framer-motion";
import { openContact } from "./ContactDrawer";
import { ArrowUpRight, Lightbulb, PenTool, Factory, ShoppingBag } from "lucide-react";

const scrollToContact = (e) => {
  e.preventDefault();
  openContact("clothing_brand");
};

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  {
    icon: Lightbulb,
    nr: "01",
    title: "Idee",
    text: "Wir entwickeln deine Clothing-Idee: Produkte, Kollektion, Konzept. Aus dem Bauchgefühl wird ein Plan.",
  },
  {
    icon: PenTool,
    nr: "02",
    title: "Design",
    text: "Skizzen, Prints, Grafiken und Logos — jedes Kleidungsstück wird konkret gestaltet, inklusive Mockups, Farben und Materialien.",
  },
  {
    icon: Factory,
    nr: "03",
    title: "Produktion",
    text: "Ich bin nicht der Hersteller — ich finde ihn: Recherche, Vergleich von Preisen, Qualität und Mindestabnahmemengen, Kontakt zu geeigneten Produktionspartnern. Ziel: gute Qualität, fairer Preis, niedrige MOQ.",
  },
  {
    icon: ShoppingBag,
    nr: "04",
    title: "Online-Shop",
    text: "Deine Brand geht online: Website, Produktseiten mit Bildern und Größen, Warenkorb, Checkout — verkaufsbereit.",
  },
];

export default function BrandAufbau() {
  return (
    <section
      id="clothing-brand"
      data-testid="brand-aufbau-section"
      className="bg-ink text-paper px-6 lg:px-12 py-24 lg:py-36 relative overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "100px" }}
          transition={{ duration: 0.75, ease: EASE }}
          className="max-w-3xl mb-16 lg:mb-24"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-goldlight font-bold mb-6">
            Kapitel 03 — Clothing Brand Aufbau
          </p>
          <h2 className="font-display uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-6xl mb-6">
            Deine eigene Marke.{" "}
            <span className="font-editorial italic normal-case tracking-normal text-goldlight">
              Von null bis verkaufsbereit.
            </span>
          </h2>
          <p className="text-base lg:text-lg leading-relaxed text-zinc-400">
            Kein Agentur-Gerede: Ich begleite dich von der ersten Clothing-Idee bis zur
            fertigen, verkaufsbereiten Brand — Design, Produktionspartner-Suche und der
            komplette Online-Shop aus einer Hand.
          </p>
        </motion.div>

        {/* Prozess-Timeline */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.nr}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.12 }}
              data-testid={`brand-step-${step.nr}`}
              className="relative bg-ink p-7 lg:p-9 flex flex-col gap-5 group hover:bg-[#141414] transition-colors duration-500"
            >
              <div className="flex items-center justify-between">
                <span className="w-11 h-11 rounded-full border border-goldlight/40 text-goldlight flex items-center justify-center group-hover:bg-goldlight group-hover:text-ink transition-colors duration-500">
                  <step.icon size={18} />
                </span>
                <span className="font-editorial italic text-4xl text-white/10 select-none">{step.nr}</span>
              </div>
              <h3 className="font-display uppercase tracking-tight text-xl lg:text-2xl">{step.title}</h3>
              <p className="text-sm leading-relaxed text-zinc-400">{step.text}</p>
              {i < STEPS.length - 1 && (
                <span className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 text-goldlight z-10">→</span>
              )}
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-10 text-center text-[11px] uppercase tracking-[0.3em] text-zinc-500"
          data-testid="brand-process-line"
        >
          Idee → Design → Produkt → Produktion → Online-Shop → Verkaufsbereit
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
          className="mt-12 flex justify-center"
        >
          <a
            href="#kontakt"
            onClick={scrollToContact}
            data-testid="brand-aufbau-cta"
            className="group relative overflow-hidden inline-flex items-center gap-3 bg-goldlight text-ink px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium"
          >
            <span className="absolute inset-0 bg-paper translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            <span className="relative flex items-center gap-3">
              Brand-Aufbau starten
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
